import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { useGetNewsQuery, useLazyGetOrganisationSettingsQuery, usePullFeedMutation, usePutNewsFeedMutation } from '../../../../Redux/Api/Organisations';
import { putFeedLocal } from '../../../../Redux/State/Organisation'
import { useAppDispatch, useAppSelector } from '../../../../Redux/store'
import { Hydra } from '../../../../Utils/Hydra'
import { useNavigate } from "react-router-dom";
import { useDeleteNewsMutation, useSetNewsToPinnedMutation } from "../../../../Redux/Api/News";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from '@mui/material'
import { faEdit, faImage, faLink, faMobileAlt, faTrashAlt } from '@fortawesome/pro-light-svg-icons'
import RowActions, { RowAction } from '../../../Datagrid/DatagridLayout/Rows/RowActions'
import ImageRenderer from '../../../Datagrid/Renderers/ImageRenderer'
import LoadingPage from '../../LoadingPage';
import StatusPopups from '../../../Shared/Form/StatusPopups';
import BusyArea from '../../../Shared/BusyArea';
import Button from '../../../Shared/Form/Button';
import Title from '../../../Shared/Title';
import Form from '../../../Shared/Form/Form';
import { AnimatePresence, motion } from 'framer-motion';
import Input from '../../../Shared/Form/Input';
import GenericDatagrid from '../../../Datagrid/GenericDatagrid';
import { faFileImport, faPlus, faThumbTack, faThumbTackSlash, faXmark } from '@fortawesome/pro-solid-svg-icons';

const NewsPage = () => {
    const { t, i18n } = useTranslation()

    const feed = useAppSelector((s) => s.organisation.settings?.news?.feed)

    const [getOrganisationsSettings, { data: settings }] = useLazyGetOrganisationSettingsQuery();

    const org_id = useAppSelector((s) => s.organisation.id)!
    const { data, refetch } = useGetNewsQuery(org_id, {
        skip: !org_id,
    })
    const [successMessage, setSuccessMessage] = useState('')
    const [successAddMessage, setSuccessAddMessage] = useState('')

    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const [putFeed] = usePutNewsFeedMutation()
    const [pullFeed] = usePullFeedMutation()
    const [deleteNews] = useDeleteNewsMutation()
    const [putPinned] = useSetNewsToPinnedMutation()

    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')

    const [selectedIcon, setSelectedIcon] = useState<string | null>(null)

    const isSmallScreen = useMediaQuery('(min-width: 650px)');

    useEffect(() => {
        if (!org_id) return;
        getOrganisationsSettings(org_id)
    }, [])

    useEffect(() => {
        if (sessionStorage.getItem('successMessage')) {
            setSuccessAddMessage(sessionStorage.getItem('successMessage')!);
            sessionStorage.removeItem('successMessage');
        }
    }, [org_id])

    useEffect(() => {
        if (org_id) {
            refetch();
        }
    }, [org_id, refetch]);

    const submit = async ({ feed }: { feed: string }) => {
        if (busy) return

        setBusy(true)
        setError('')
        setSuccessMessage('')

        try {
            await putFeed({ id: org_id, feed }).unwrap()

            await pullFeed(org_id).unwrap()

            refetch()

            dispatch(putFeedLocal(feed))
            setSuccessMessage(t('organisation:news:successImport') as string)
        } catch (e) {

            if ((e as { data: { 'hydra:description': string } }).data['hydra:description'] === 'No new items to import. RSS feed is up to date.') {
                setSuccessMessage(t('organisation:news:alreadyImported') as string)
            } else {
                setError(t('error') as string)
            }

            setBusy(false)
        }

        setTimeout(() => setBusy(false), 200)
    }

    const togglePin = useCallback(async (id: string, pinned: boolean) => {
        if (busy) return

        setBusy(true)
        setError('')
        setSuccessMessage('')

        try {
            await putPinned({ id: id, pinned: pinned }).unwrap()

            refetch()

            if (pinned) {
                setSuccessMessage(t('organisation:news:successNewsPinned') as string)
            } else {
                setSuccessMessage(t('organisation:news:successNewsUnpinned') as string)
            }
        } catch (e) {
            setError(t('modal:error') as string);
            setBusy(false);
        }

        setTimeout(() => setBusy(false), 200)
    }, [busy, putPinned, refetch, t])

    const deleteNewsAction = async (id: string) => {
        if (busy) return

        setBusy(true)
        setError('')
        setSuccessMessage('')

        try {
            await deleteNews(id)

            if (org_id)
                refetch()
        } catch (e) {
            let err = e as Hydra.Error
            setError(err['hydra:description'])
        }

        setBusy(false)
    }

    const showIcon = useCallback((icon: string) => {
        setSelectedIcon(icon);
    }, []);

    const closeIconPreview = useCallback(() => {
        setSelectedIcon(null);
    }, []);

    const columns = [
        {
            field: 'pinned',
            headerName: '',
            renderCell: (params: any) => (
                <span className='relative flex items-center justify-center w-full h-full'>
                    <button
                        type='button'
                        className='flex items-center justify-center transition hover:bg-black/20 min-w-10 min-h-10 rounded-full'
                        onClick={(e) => { e.stopPropagation(); togglePin(params.row.id, !params.row.pinned); }}
                    >
                        <FontAwesomeIcon icon={params.row.pinned ? faThumbTack : faThumbTackSlash} className={`text-[20px] ${params.row.pinned ? 'text-black' : 'text-black/40'}`} />
                    </button>
                </span>
            ),
            maxWidth: 50,
            minWidth: 50,
            filterable: false,
        },
        {
            field: 'image',
            headerName: '',
            renderCell: (params: any) => (
                <ImageRenderer src={params.row.image} alt={params.row.title} onClick={() => showIcon(params.row.image)} />
            ),
            maxWidth: 60,
            minWidth: 60,
            filterable: false,
        },
        {
            field: 'title',
            headerName: t('organisation:news:tableTitle'),
            flex: 1,
        },
        {
            field: 'url',
            headerName: t('organisation:news:url'),
            flex: 1,
            hide: !isSmallScreen,
        },
        {
            field: 'date',
            headerName: t('datagrid:createdAt'),
            flex: 1,
            valueGetter: (params: any) => (
                moment(params.row.date).format('DD-MM-YYYY')
            ),
            hide: !isSmallScreen,
            type: "date",
        },
        {
            field: 'actions',
            headerName: '',
            width: isSmallScreen ? 120 : 80,
            renderCell: (params: any) => {
                const actions: RowAction[] = [
                    {
                        label: t('revealImage'),
                        icon: <FontAwesomeIcon icon={faImage} />,
                        onClick: () => { showIcon(params.row.image); }
                    },
                    {
                        label: t('cards:list:adjust'),
                        icon: <FontAwesomeIcon icon={faEdit} />,
                        onClick: () => navigate(`/${i18n.language}/dashboard/news/edit/` + params.row.id),
                    },
                    {
                        label: t('cards:list:delete'),
                        icon: <FontAwesomeIcon icon={faTrashAlt} />,
                        withModal: {
                            onYes: () => { deleteNewsAction(params.row.id); }
                        }
                    }
                ];

                if (settings?.notification) {
                    actions.unshift({
                        label: t('makeNotification'),
                        icon: <FontAwesomeIcon icon={faMobileAlt} />,
                        onClick: () => navigate(`/${i18n.language}/dashboard/notify`, { state: { news: { title: params.row.title, url: params.row.url, text: params.row.text } } }),
                    });
                }

                if (params.row.url) {
                    actions.unshift({
                        label: t('openLink'),
                        icon: <FontAwesomeIcon icon={faLink} />,
                        withModal: {
                            title: t('openLinkQuestion') as string,
                            body: params.row.url,
                            onYes: () => { window.open(params.row.url, '_blank'); }
                        }
                    });

                }

                return <RowActions actions={actions} small={!isSmallScreen} />;
            },
        }
    ];

    if (!data) return <LoadingPage />

    return (
        <>
            <div className='flex flex-wrap justify-between items-center mb-3'>
                <Title text={t('organisation:news:title')} subtitle={t('organisation:news:subtitle') as string} />
                <Button
                    title={t('organisation:news:add')}
                    icon={faPlus}
                    iconright
                    action={() => navigate(`/${i18n.language}/dashboard/news/create`)}
                />
            </div>

            <StatusPopups setText={setError} type='error' text={error} />
            <StatusPopups setText={setSuccessMessage} type='success' text={successMessage} />
            <StatusPopups setText={setSuccessAddMessage} type='success' text={successAddMessage} />
            <BusyArea busy={busy}>
                <Form submit={submit} nostyle>
                    <div className='flex flex-col sm:flex-row gap-2 sm:gap-3'>
                        <Input
                            tooltip={`${t('organisation:news:rssMessageDescription')} ${t('organisation:news:ask')}.`}
                            tooltipOnClick={() => window.open('https://x-interactive.nl/contact/', '_blank')}
                            className='mt-2 w-full mb-4'
                            id='feed'
                            type='url'
                            placeholder='https://'
                            initial={feed}
                            label='RSS Feed URL' />
                        <div className='mt-0 sm:mt-[36px] mb-6 sm:mb-0'>
                            <Button icon={faFileImport} title={t('organisation:news:import')} className='whitespace-nowrap' />
                        </div>
                    </div>
                </Form>
                <GenericDatagrid
                    data={data.news}
                    columns={columns}
                    onRowClick={(params: any) => navigate(`/${i18n.language}/dashboard/news/edit/` + params.row.id)}
                    className='mb-5 lg:mb-10'
                    disableHeaderCells={['image', 'pinned']}
                />

                <AnimatePresence>
                    {selectedIcon && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            onClick={closeIconPreview}
                            className="fixed top-0 left-0 w-full h-full bg-black/50 flex justify-center items-center z-[1000]">
                            <FontAwesomeIcon icon={faXmark}
                                className="absolute top-4 right-4 text-4xl text-white cursor-pointer" />
                            <motion.img initial={{ scale: 0.5 }} animate={{ scale: 1 }} exit={{ scale: 1.2 }}
                                src={selectedIcon} alt="Sponsor Logo Preview"
                                className="max-h-[calc(100vh-100px)] max-w-[calc(100vw-100px)]" />
                        </motion.div>
                    )}
                </AnimatePresence>
            </BusyArea>
        </>
    )
}

export default NewsPage
